/**
 * Тосты редактора не держат ленту под собой (проба P50m20f, E6).
 *
 * Тост «Правка принята (папка 1)» в правом нижнем углу лёг поверх ♪ той же строки: второй клик ♪ через 9 с после первого
 * попал в текст тоста, ♪ не сработал, а тост, пока указатель стоял на нём, не уходил (antd держит тост под указателем) —
 * его пришлось закрывать крестиком. Справа внизу ленты всегда что-то есть: ♪ и 🔊 строк перевода, дорожка озвучки с
 * ручками, текст реплик, — свободного места для тоста там нет. Поэтому тост остаётся на своём месте и таким же, но:
 *   • нажатие на его текст проходит сквозь него — в ♪, ручку, текст реплики под ним; кнопки тоста (крестик, «Сократить
 *     свой текст», ссылки) нажимаются, как прежде;
 *   • пока указатель над тостом (не над его кнопкой), тост почти прозрачен — видно, что под ним; ушёл указатель —
 *     тост прежний.
 * Тосты складываются в свой слой `x_toasts` (getContainer у держателей тостов редактора), правила — только для него.
 */
import "./toastLayer.css";

export const TOAST_ROOT_CLASS = "x_toasts";
/** Класс списка тостов, над которым сейчас указатель: список почти прозрачен (toastLayer.css). */
export const TOAST_YIELD_CLASS = "x_toast_yield";

const LIST = ".ant-notification";
const CARD = ".ant-notification-notice-wrapper";

/** Слой тостов редактора — один на страницу, последним в body. */
export const toastContainer = (doc: Document = document): HTMLElement => {
    const found = Array.from(doc.body.children).find((child) => child.classList.contains(TOAST_ROOT_CLASS));
    if (found) return found as HTMLElement;
    const root = doc.createElement("div");
    root.className = TOAST_ROOT_CLASS;
    root.setAttribute("data-toasts", "pass-through");
    doc.body.appendChild(root);
    return root;
};

/** Держателю тостов (`notification.useNotification`): тосты — в слой, который не держит ленту. */
export const TOAST_LAYER_CONFIG = { getContainer: () => toastContainer() };

const inside = (rect: { left: number; right: number; top: number; bottom: number }, x: number, y: number) =>
    x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * Какие списки тостов уступают указателю: указатель над карточкой тоста, но не над его кнопкой (`target` — элемент под
 * указателем; текст тоста нажатий не ловит, поэтому `target` — то, что под тостом, а у кнопки тоста — сама кнопка).
 */
export const yieldToasts = (root: ParentNode, x: number, y: number, target: EventTarget | null): void => {
    const lists = Array.from(root.querySelectorAll(LIST));
    for (const list of lists) {
        const onOwnControl = !!target && target instanceof Node && list.contains(target);
        const under =
            !onOwnControl &&
            Array.from(list.querySelectorAll(CARD)).some((card) => inside(card.getBoundingClientRect(), x, y));
        if (under) list.classList.add(TOAST_YIELD_CLASS);
        else list.classList.remove(TOAST_YIELD_CLASS);
    }
};

/** Указатель ушёл со страницы — тосты прежние. */
export const restoreToasts = (root: ParentNode): void => {
    Array.from(root.querySelectorAll(`.${TOAST_YIELD_CLASS}`)).forEach((list) => list.classList.remove(TOAST_YIELD_CLASS));
};

type Schedule = (run: () => void) => void;
const nextFrame: Schedule = (run) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(run);
    } else {
        run();
    }
};

/**
 * Следить за указателем: над тостом — тост уступает (почти прозрачен), ушёл — прежний. Пересчёт — раз в кадр, по
 * последнему положению; новый или ушедший тост под стоящим указателем — тоже (слой тостов поменялся).
 */
export const watchToastYield = (doc: Document = document, schedule: Schedule = nextFrame): (() => void) => {
    let last: { x: number; y: number; target: EventTarget | null } | null = null;
    let queued = false;
    const update = () => {
        queued = false;
        if (!last) return;
        yieldToasts(toastContainer(doc), last.x, last.y, last.target);
    };
    const queue = () => {
        if (queued) return;
        queued = true;
        schedule(update);
    };
    const onMove = (event: MouseEvent) => {
        last = { x: event.clientX, y: event.clientY, target: event.target };
        queue();
    };
    const onOut = (event: MouseEvent) => {
        if (event.relatedTarget) return;
        last = null;
        restoreToasts(toastContainer(doc));
    };
    doc.addEventListener("mousemove", onMove, { passive: true });
    doc.addEventListener("mouseout", onOut);
    const observer =
        typeof MutationObserver === "function" ? new MutationObserver(() => last && queue()) : null;
    if (observer) observer.observe(toastContainer(doc), { childList: true, subtree: true });
    return () => {
        doc.removeEventListener("mousemove", onMove);
        doc.removeEventListener("mouseout", onOut);
        if (observer) observer.disconnect();
    };
};
